import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Mail, Bell, BellRing, CircleDollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { sendManualReminder, recordInvoicePayment, getInvoicePayments, deleteInvoicePayment, getARReminderSettings, togglePauseReminders } from "@/lib/api";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { onlinePaymentsEnabled, emailIntro } from "@/lib/onlinePayments";
import { getEmailDocMode, docFilename, withDocName } from "@/components/common/EmailDocMode";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { shortDate, money } from "@/lib/format";

const errMsg = (e) => e?.response?.data?.detail || e?.message || "Failed";

// Remind now (mail) · Auto reminders (bell) · Record payment — same pattern as Lien Actions; shared by Invoicing and the Ops Dashboard AR card.
export function InvoiceActionButtons({ r, canWrite = true, onRemind, onPayment, onChanged, remindTitle = "Email payment reminder now" }) {
  const canRemind = canWrite && r.status === "sent" && r.balance !== 0;
  const title = r.status !== "sent" ? "Reminders apply to sent invoices" : `${remindTitle}${r.last_reminder_at ? ` · last sent ${shortDate(String(r.last_reminder_at).slice(0, 10))}` : ""}`;
  return (
    <>
      <Button size="sm" variant="ghost" className={`h-7 w-7 p-0 ${r.reminder_count > 0 ? "text-amber-700" : ""}`} title={title} aria-label="Email payment reminder" onClick={(e) => { e.stopPropagation(); onRemind(r); }}
        disabled={!canRemind} data-testid={`btn-remind-${r.id}`}>
        <Mail className="w-3.5 h-3.5" />
      </Button>
      <InvoiceAutoBell row={r} disabled={!canWrite || r.status === "void"} onChanged={onChanged} />
      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-700" title="Record payment" aria-label="Record Payment"
        onClick={(e) => { e.stopPropagation(); onPayment(r); }} disabled={!canWrite || r.status === "void"} data-testid={`btn-payment-${r.id}`}>
        <CircleDollarSign className="w-3.5 h-3.5" />
      </Button>
      {r.reminder_count > 0 && <span className="ml-0.5 text-[10px] text-amber-700 num" data-testid={`reminder-count-${r.id}`}>{r.reminder_count}</span>}
    </>
  );
}

const DEFAULT_TIERS = [{ tier: 1, days: 31, label: "Friendly Reminder" }, { tier: 2, days: 46, label: "Second Notice" }, { tier: 3, days: 60, label: "Account Hold Notice" }, { tier: 4, days: 61, label: "Daily Past Due Reminder" }];

// Bell: per-invoice switch for the automated AR reminder tiers (Settings → AR reminders). Off = reminders_paused.
export function InvoiceAutoBell({ row, disabled, onChanged }) {
  const [open, setOpen] = useState(false);
  const [tiers, setTiers] = useState(null);
  const [saving, setSaving] = useState(false);
  const on = !row.reminders_paused && row.status === "sent";
  useEffect(() => {
    if (!open || tiers) return;
    getARReminderSettings()
      .then(({ data }) => { const t = (data?.tiers || []).filter((x) => x.enabled !== false); setTiers(t.length ? t : DEFAULT_TIERS); })
      .catch(() => setTiers(DEFAULT_TIERS));
  }, [open, tiers]);
  const toggle = async (v) => {
    setSaving(true);
    try { await togglePauseReminders(row.id, !v); onChanged?.(row.id, { reminders_paused: !v }); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const title = on
    ? `Auto reminders ON — tiers from Settings → AR reminders${row.last_reminder_at ? ` · last sent ${shortDate(String(row.last_reminder_at).slice(0, 10))}` : ""}`
    : "Auto reminders OFF for this invoice — click to set up";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title={title} disabled={disabled} onClick={(e) => e.stopPropagation()} data-testid={`btn-auto-reminders-${row.id}`}
          className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors disabled:opacity-40 ${on ? "text-amber-600 hover:bg-amber-50" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}>
          {on ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-3" align="end" onClick={(e) => e.stopPropagation()} data-testid={`auto-reminders-popover-${row.id}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-semibold text-slate-700">Automated reminders</div>
            <div className="text-[10px] text-slate-400">Emails each tier as the invoice ages, until paid</div>
          </div>
          <Switch checked={on} disabled={saving || row.status !== "sent"} onCheckedChange={toggle} data-testid={`auto-reminders-switch-${row.id}`} />
        </div>
        <div className="space-y-1">
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Schedule</div>
          {tiers === null ? <div className="text-[11px] text-slate-400">Loading…</div> : tiers.length === 0 ? (
            <div className="text-[11px] text-slate-500">No tiers enabled — set them up in Settings → Email → AR reminders.</div>
          ) : (
            <ul className="space-y-0.5 text-[11px] text-slate-600">
              {tiers.map((t) => (
                <li key={t.tier} className="flex items-center justify-between" data-testid={`auto-reminder-tier-${t.tier}-${row.id}`}>
                  <span className={(row.last_reminder_tier || 0) >= t.tier ? "line-through text-slate-400" : ""}>{t.label || `Tier ${t.tier}`}</span>
                  <span className="num text-slate-400">day {t.days}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="text-[10px] text-slate-400 num">
          {row.status !== "sent" ? "Reminders start once the invoice is sent" : row.last_reminder_at ? `Last reminder ${shortDate(String(row.last_reminder_at).slice(0, 10))} · ${row.reminder_count || 0} sent` : "No reminders sent yet"}
          {saving && <span className="ml-1 text-slate-500">· saving…</span>}
        </div>
        {!row.customer_email && (
          <div className="rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[10px] text-rose-700" data-testid={`auto-reminders-noemail-${row.id}`}>
            No customer email on this invoice — reminders have nowhere to go. Add a billing email first.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Resend = hand a pre-filled draft to the operator's own mail client.
export async function resendInvoiceEmail(r) {
  const base = process.env.REACT_APP_BACKEND_URL;
  const docName = docFilename("Invoice", r.invoice_number);
  const attach = getEmailDocMode() === "attach";
  const url = attach ? `(${docName} attached)` : withDocName(r.short_code ? `${base}/api/r/i/${r.short_code}` : `${base}/api/invoices/${r.id}/pdf`, docName);
  if (!window.confirm(`${attach ? "Download the PDF and open" : "Open"} an email draft for invoice ${r.invoice_number} to ${r.customer_email || "the customer"}?`)) return;
  if (attach) downloadPdfUrl(`/invoices/${r.id}/pdf?download=1`, { suggestedName: docName, label: "invoice" }).catch(() => {});
  const payOn = await onlinePaymentsEnabled();
  const subject = encodeURIComponent(`Invoice ${r.invoice_number} from ${COMPANY_INFO.name}`);
  const body = encodeURIComponent(
    `Hi ${r.customer_contact || "there"},\n\n` +
    `${emailIntro("invoice", payOn)}\n\n` +
    `Invoice ${r.invoice_number}\n${url}\n\nThank you,\n${COMPANY_INFO.name}`,
  );
  window.location.href = `mailto:${r.customer_email || ""}?subject=${subject}&body=${body}`;
}

// Emails the next reminder tier from Settings → AR reminders. Returns the log entry, or null if cancelled/failed.
export async function sendInvoiceReminder(r) {
  if (!window.confirm(`Send a payment reminder for invoice ${r.invoice_number} to ${r.customer_email || "the customer on file"}?`)) return null;
  try {
    const tier = (r.last_reminder_tier || 0) + 1;
    const { data } = await sendManualReminder(r.id, { tier, tier_label: "Manual reminder", use_settings_template: true });
    if (data?.status === "sent") toast.success(`Reminder sent for ${r.invoice_number}`);
    else toast.warning(`Reminder logged but not emailed (${data?.status || "unknown"}${data?.error ? `: ${data.error}` : ""})`);
    return data;
  } catch (e) { toast.error(errMsg(e)); return null; }
}

export function PaymentDialog({ row, onClose, onChanged }) {
  const [state, setState] = useState(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("check");
  const [reference, setReference] = useState("");
  const [receivedAt, setReceivedAt] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);

  const edited = useRef(false);
  const load = useCallback(async (prefill = true) => {
    try {
      const { data } = await getInvoicePayments(row.id);
      setState(data);
      if (prefill && !edited.current) setAmount(data.balance > 0 ? String(data.balance.toFixed(2)) : "");
    } catch (e) { toast.error(errMsg(e)); }
  }, [row.id]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await recordInvoicePayment(row.id, {
        amount: Number(amount), method, reference,
        received_at: new Date(`${receivedAt}T12:00:00Z`).toISOString(),
      });
      toast.success("Payment recorded");
      edited.current = false;
      await load();
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Remove this payment? The invoice balance and status will be recalculated.")) return;
    try {
      await deleteInvoicePayment(row.id, pid);
      toast.success("Payment removed");
      await load();
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="dialog-invoice-payments">
        <DialogHeader>
          <DialogTitle>Payments — invoice {row.invoice_number}</DialogTitle>
          <DialogDescription>
            {row.customer_name} · invoice total {money(row.amount ?? row.total)}. Partial payments are supported;
            the invoice flips to Paid on its own once the balance reaches zero.
          </DialogDescription>
        </DialogHeader>

        {state && (
          <div className="flex gap-4 text-sm" data-testid="payment-totals">
            <div><span className="text-slate-500">Paid</span> <span className="font-semibold">{money(state.amount_paid)}</span></div>
            <div><span className="text-slate-500">Balance</span> <span className="font-semibold">{money(state.balance)}</span></div>
          </div>
        )}

        {state?.payments?.length > 0 && (
          <div className="space-y-1 max-h-40 overflow-y-auto text-sm">
            {state.payments.map(p => (
              <div key={p.id} className="flex items-center gap-2 border-b border-slate-100 pb-1" data-testid={`payment-row-${p.id}`}>
                <span className="tabular-nums font-medium">{money(p.amount)}</span>
                <span className="text-xs text-slate-500">{p.method}{p.reference ? ` · ${p.reference}` : ""}</span>
                {p.source === "foundation" && (
                  <span className="text-[9px] uppercase tracking-wide text-emerald-700 border border-emerald-200 rounded px-1" title={p.note || "Synced from Foundation cash receipts"}
                    data-testid={`payment-fdn-${p.id}`}>FDN</span>
                )}
                <span className="text-xs text-slate-400 num">{shortDate(String(p.received_at || "").slice(0, 10))}</span>
                <button className="ml-auto text-xs text-rose-600 underline" onClick={() => remove(p.id)}
                  title={p.source === "foundation" ? "Removes it here only — it comes back on the next nightly pull if Foundation still shows the cash" : undefined}
                  data-testid={`btn-remove-payment-${p.id}`}>remove</button>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={add} className="grid grid-cols-2 gap-2 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Amount</Label>
            <Input type="number" step="0.01" min="0.01" required value={amount}
              onChange={(e) => { edited.current = true; setAmount(e.target.value); }} data-testid="input-payment-amount" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-9" data-testid="select-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["check", "ach", "credit card", "cash", "other"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Reference</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Check #"
              data-testid="input-payment-reference" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Received</Label>
            <Input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)}
              data-testid="input-payment-date" />
          </div>
          <DialogFooter className="col-span-2 gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="btn-close-payments">Close</Button>
            <Button type="submit" disabled={busy || !amount} data-testid="btn-add-payment">
              {busy ? "Saving…" : "Record payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
